import { compact, defer } from 'lodash-es';
import { TinyEmitter } from 'tiny-emitter';
import router, { getRoot, getUri, updatePageLinks } from 'core/router';
import pageMap from 'core/pageMap';
import store from 'store';
import promise from 'helpers/promise';
import mqStore from 'store/mqStore';
const reloadAbTasty = () => {
    // document.querySelector('[data-abtest]')?.remove()
    // const styleTag = document.createElement('style')
    // styleTag.innerText = '.product-gallery__list {display: grid;grid-template-columns: repeat(2, 1fr);}'
    // styleTag.setAttribute('data-abtest', '')
    // document.head?.append(styleTag);
    window?.ABTastyReload?.();
};
class PageManager extends TinyEmitter {
    main = true;
    disabled = false;
    loadBeforeHide;
    crossTransition;
    currentPage;
    previousPage;
    nextPageInfo;
    xhr;
    pathName;
    newPathName;
    container;
    pageSelector;
    defaultPageClass;
    parameters;
    state = {
        transitioning: false,
        loading: false,
        init: false
    };
    constructor(container, pageSelector, defaultPageClass, parameters = {}, { loadBeforeHide = true, // ⚠ Should be true if you want to use virtual page manager
    crossTransition = true } = {}) {
        super();
        // Page Parameters
        this.parameters = parameters;
        // Transition Parameters
        this.loadBeforeHide = loadBeforeHide;
        this.crossTransition = crossTransition;
        // Manager Parameters
        this.container = container;
        this.defaultPageClass = defaultPageClass;
        this.pageSelector = pageSelector;
        this.initializeRoutes();
    }
    async initializePage() {
        if (this.main) {
            this.extractPageInfo(document);
            this.pathName = getUri();
            if (this.nextPageInfo && this.nextPageInfo.bodyClassName.indexOf('error404') > -1)
                this.pathName = '404';
        }
        const el = this.extractPage(this.container);
        if (!el)
            return;
        const pageClass = await this.getPageClass(el);
        this.currentPage = this.createPage(el, pageClass);
        const prev = this.state.previous;
        if (!this.currentPage)
            return;
        this.currentPage?.restore?.();
        updatePageLinks();
        document.querySelectorAll('a[href="#"]:not([class*="anbu"]):not([id*="anbu"]):not([id="store-reservation"])').forEach((a) => { a.classList.add('ignored-link'); });
        await promise.wait(1);
        await this.currentPage.askPreload(prev);
        this.state.init = true;
        this.emit('init');
        await promise.wait(1);
        this.emit('show', this.currentPage);
        await this.currentPage.askShow(prev, el);
        this.currentPage.transitionComplete();
    }
    initializeRoutes() {
        router.on('**', (event) => {
            const pathName = event ? compact([event.url, event.queryString]).join('?') : '';
            this.onRouteUpdate('/' + pathName);
        });
        defer(() => router.resolve());
    }
    virtual(pathName, requestOptions) {
        this.emit('virtual', pathName);
        this.rewriteRoute(pathName, false);
        this.onRouteUpdate(pathName, requestOptions);
    }
    rewriteRoute(pathName, overwrite = true, replaceState = false) {
        if (~pathName.indexOf(getRoot()))
            pathName = pathName.replace(getRoot(), '');
        const historyAPIMethod = replaceState ? 'replaceState' : 'pushState';
        if (overwrite) {
            router.navigate(pathName, { callHandler: false, callHooks: false, historyAPIMethod });
        }
        else {
            if (pathName === this.newPathName)
                return;
            this.disabled = true;
            router.navigate(pathName, { callHandler: false, callHooks: false, historyAPIMethod });
            this.disabled = false;
        }
    }
    inject(pathName, xhr, requestOptions = {}) {
        const cb = () => {
            this.rewriteRoute(pathName, false);
            this.pageLoaded(pathName, xhr, requestOptions);
        };
        if (this.state.transitioning || this.state.loading)
            this.once('shown', cb);
        else
            cb();
    }
    navigateTo(pathName) {
        router.navigate(pathName);
    }
    onRouteUpdate(pathName, requestOptions = {}) {
        if (this.disabled)
            return false;
        if (!this.currentPage && this.extractPage(this.container) && !this.previousPage) {
            this.initializePage();
            return true;
        }
        pathName = pathName.split('#')[0];
        // if (this.state.transitioning || this.state.loading) {
        //   this.rewriteRoute(this.newPathName as string)
        //   this.waitingForPage = pathName
        //   return
        // }
        this.newPathName = pathName;
        this.emit('navigate', pathName);
        if (!this.loadBeforeHide) {
            if (this.currentPage)
                this.previousPage = this.currentPage;
            if (this.previousPage)
                this.hidePage();
        }
        this.emit('loading');
        this.load(pathName, requestOptions);
        return true;
    }
    load(pathName, { method = 'GET', body, ...options } = {}) {
        this.emit('load:start');
        if (this.xhr) {
            this.xhr.onload = this.xhr.onerror = null;
            this.xhr.abort();
        }
        this.state.loading = true;
        document.documentElement.classList.add('loading');
        this.xhr = new XMLHttpRequest();
        this.xhr.withCredentials = true;
        this.xhr.open(method, pathName, true);
        this.xhr.setRequestHeader('X-Fursac-Ajax', 'true');
        this.xhr.responseType = 'document';
        this.xhr.onload = (event) => {
            this.pageLoaded(pathName, event.target, options);
            this.emit('load:end');
            // document.documentElement.classList.remove('loading')
        };
        this.xhr.send(body);
    }
    triggerInternalRouting(pathName, xhr, requestOptions) {
        requestOptions.rewriteRoute = this.rewriteRoute.bind(this);
        this.state.loading = false;
        this.currentPage?.internalRouting?.(pathName, xhr, requestOptions);
    }
    cancelTransition() {
        this.rewriteRoute(this.pathName, true);
        this.state.loading = false;
        this.emit('load:end');
        this.emit('loaded');
        document.documentElement.classList.remove('loading');
    }
    async pageLoaded(pathName, xhr, requestOptions) {
        const page = xhr.response;
        const el = this.extractPageFromXHR(page.body);
        this.state.loading = false;
        document.documentElement.classList.remove('loading');
        // DETECT ERROR
        if (!el) {
            console.error('PageManager: No page found in response'); // eslint-disable-line
            document.documentElement.innerHTML = page.documentElement.innerHTML; // Unhandled errors
            return false;
        }
        // INJECT IN ANOTHER ROUTER
        if (el.hasAttribute('data-router')) {
            const routerKey = el.getAttribute('data-router');
            el.removeAttribute('data-router');
            const router = store.routers.get()[routerKey];
            if (router && router !== this) {
                router.inject(pathName, xhr, requestOptions);
                this.cancelTransition();
                return false;
            }
        }
        const PageClass = await this.getPageClass(el);
        const current = this.currentPage;
        // INTERNAL ROUTING
        if (current &&
            current.pageName() === PageClass.pageName &&
            current.internalRouting &&
            current.shouldRouteInternally?.(el, pathName)) {
            this.triggerInternalRouting(pathName, xhr, requestOptions);
            return false;
        }
        this.pathName = xhr.responseURL;
        this.state.next = PageClass.pageName;
        if (xhr.responseURL !== pathName && ~xhr.responseURL.indexOf(getRoot())) {
            const newPath = xhr.responseURL.replace(getRoot(), '');
            if (requestOptions.rewriteRoute)
                requestOptions.rewriteRoute(newPath, false, true);
            else
                this.rewriteRoute(newPath, false, true);
        }
        this.emit('loaded', page);
        this.extractPageInfo(page);
        this.preloadPage(el, PageClass);
        xhr.onload = null;
        return true;
    }
    extractPageInfo(page) {
        this.nextPageInfo = {
            title: page.title,
            bodyClassName: page.body.className
        };
    }
    async preloadPage(el, PageClass) {
        if (this.loadBeforeHide && this.currentPage)
            this.previousPage = this.currentPage;
        const page = this.createPage(el, PageClass);
        this.currentPage = page;
        const previousPage = this.previousPage && this.previousPage.pageName();
        this.container.appendChild(page.el);
        await promise.wait(1);
        await page.askPreload(previousPage);
        this.state.loading = false;
        document.documentElement.classList.remove('loading');
        this.state.transitioning = true;
        if (this.previousPage) {
            if (this.previousPage?.state.hidden)
                this.pageHidden();
            else if (this.loadBeforeHide)
                this.hidePage();
            if (this.crossTransition)
                this.showPage();
        }
        else {
            this.showPage();
        }
    }
    async showPage() {
        if (!this.currentPage)
            return;
        reloadAbTasty();
        const previousPage = this.state.previous;
        if (this.main) {
            const bodyClassName = this.nextPageInfo?.bodyClassName || '';
            const classes = bodyClassName.split(' ');
            const variants = classes.filter(c => c.includes('header__')).map(c => c.replace('header__', ''));
            const otherClasses = classes.filter(c => !c.includes('header__'));
            document.body.className = otherClasses.join(' ');
            store.headerVariants.set(variants);
            if (this.nextPageInfo?.notification)
                document.body.prepend(this.nextPageInfo?.notification || '');
            else
                document.body.querySelector('notification')?.remove();
            if (this.nextPageInfo?.title)
                document.title = this.nextPageInfo?.title || '';
        }
        await promise.wait();
        updatePageLinks();
        this.emit('show', this.currentPage);
        await this.currentPage.askShow(previousPage, this.previousPage?.el);
        this.pageShown();
    }
    transitionComplete() {
        if (!this.state.transitioning)
            return;
        this.state.transitioning = false;
        this.emit('shown', this.currentPage);
        this.currentPage?.transitionComplete();
    }
    pageShown = async () => {
        this.currentPage.emit('show:complete');
        if (this.previousPage && this.previousPage.isCheckoutInternalPage && !mqStore.tabletPortrait.get())
            await promise.wait(1000);
        if (this.crossTransition && this.previousPage)
            this.removePage();
        this.transitionComplete();
        // if (this.waitingForPage) {
        //   this.navigateTo(this.waitingForPage)
        //   this.waitingForPage = null
        // }
    };
    async hidePage() {
        if (!this.previousPage)
            return;
        const nextPage = this.state.next;
        this.state.previous = this.previousPage.pageName();
        this.emit('hide', this.previousPage);
        await this.previousPage.askHide(nextPage, this.currentPage?.el);
        this.pageHidden();
    }
    pageHidden = () => {
        if (!this.loadBeforeHide && this.state.loading)
            return;
        if (this.crossTransition) {
            if (!this.currentPage || this.currentPage.state.shown) {
                this.removePage();
                this.transitionComplete();
            }
        }
        else {
            this.removePage();
            if (this.loadBeforeHide || this.currentPage)
                this.showPage();
        }
    };
    removePage() {
        if (!this.previousPage)
            return;
        this.container.removeChild(this.previousPage.el);
        this.previousPage.flush();
        this.previousPage = undefined;
    }
    async getPageClass(el) {
        if (!el)
            return this.defaultPageClass;
        const pageClassName = el.getAttribute('data-page') || '';
        // const pageMap = require('core/pageMap').default as Record<string, IPageClass>
        // const pageMap = (await import('core/pageMap'))?.default as Record<string, IPageClass>
        const firstCharToLowerCase = (str) => str.charAt(0).toLowerCase() + str.slice(1);
        const pageName = pageClassName && firstCharToLowerCase(pageClassName);
        return pageMap[pageName] || this.defaultPageClass;
    }
    extractPageFromXHR(el) {
        return this.extractPage(el);
    }
    extractPage(el) {
        return el?.querySelector(this.pageSelector);
    }
    createPage(el, PageClass) {
        const page = new PageClass(el, this.parameters, this);
        return page;
    }
    resize = () => {
        if (this.currentPage)
            this.currentPage.resize();
        if (this.previousPage)
            this.previousPage.resize();
    };
    flush() {
        if (this.currentPage)
            this.currentPage.flush();
        if (this.previousPage)
            this.previousPage.flush();
    }
}
export default PageManager;
